import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import EventCard from "@/components/base/EventCard";
import Breadcrumb from "@/components/base/Breadcrumb";
import usePageMeta from "@/hooks/usePageMeta";
import { categories, events } from "@/mocks/events";

export default function Events() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeCat = searchParams.get("cat") ?? "all";
  const query = searchParams.get("q") ?? "";
  const [input, setInput] = useState(query);

  const activeCategory = categories.find((c) => c.key === activeCat) ?? categories[0];
  const isAll = activeCat === "all";

  usePageMeta({
    title: isAll
      ? "瓜田广场 · 热门吃瓜事件大全与网友热评合集"
      : `${activeCategory.name}热门吃瓜事件 · 瓜田广场`,
    description: isAll
      ? "瓜田广场汇总全部正在围观的热门吃瓜事件，按娱乐圈、社会热点、日常生活、科技数码、体育赛事分类整理，每条事件都附有网友热评与高赞神回复。"
      : `${activeCategory.name}分类下的热门吃瓜事件合集：${activeCategory.desc}，附网友热评与高赞神回复整理。`,
    keywords: `热门吃瓜,${activeCategory.name}吃瓜,热门吃瓜评论区,网友热评,高赞神回复`,
    path: "/events",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: isAll ? "瓜田广场 · 热门吃瓜事件大全" : `${activeCategory.name}热门吃瓜事件`,
      description: "按分类整理的热门吃瓜事件列表，含网友热评与高赞神回复入口。",
      inLanguage: "zh-CN",
      breadcrumb: {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: "/" },
          { "@type": "ListItem", position: 2, name: "瓜田广场", item: "/events" },
        ],
      },
      mainEntity: {
        "@type": "ItemList",
        itemListElement: events.map((e, i) => ({
          "@type": "ListItem",
          position: i + 1,
          name: e.title,
          url: `/event/${e.id}`,
        })),
      },
    },
  });

  const filtered = useMemo(() => {
    const kw = query.trim().toLowerCase();
    return events.filter((e) => {
      const matchCat = activeCat === "all" || e.category === activeCat;
      const matchKw =
        !kw ||
        e.title.toLowerCase().includes(kw) ||
        e.summary.toLowerCase().includes(kw) ||
        e.tags.some((t) => t.toLowerCase().includes(kw));
      return matchCat && matchKw;
    });
  }, [activeCat, query]);

  const selectCategory = (key: string) => {
    const next = new URLSearchParams(searchParams);
    if (key === "all") next.delete("cat");
    else next.set("cat", key);
    setSearchParams(next);
  };

  const submitSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const next = new URLSearchParams(searchParams);
    if (input.trim()) next.set("q", input.trim());
    else next.delete("q");
    setSearchParams(next);
  };

  return (
    <Layout>
      <section className="w-full border-b border-background-200 bg-background-100 px-4 py-10 md:px-8 md:py-14">
        <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "瓜田广场" }]} />
        <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-2xl">
            <h1 className="font-heading text-3xl font-black tracking-tight text-foreground-950 md:text-4xl">
              {isAll ? "瓜田广场 · 全部围观现场" : `${activeCategory.name}热评现场`}
            </h1>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600 md:text-base">
              {isAll
                ? "这里是所有正在被围观的热门吃瓜事件。挑一个感兴趣的现场点进去，就能看到整理好的网友热评和高赞神回复。"
                : `${activeCategory.desc}。下面是该分类下最受关注的事件，评论区同样精彩。`}
            </p>
          </div>

          <form onSubmit={submitSearch} className="w-full lg:w-80">
            <label htmlFor="event-search" className="mb-2 block text-xs font-semibold text-foreground-600">
              搜索事件或关键词
            </label>
            <div className="flex items-center gap-2 rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 transition-colors focus-within:border-primary-400">
              <i className="ri-search-line text-base text-foreground-500"></i>
              <input
                id="event-search"
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="例如：机场、外卖、发布会"
                className="w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
              />
              <button
                type="submit"
                className="shrink-0 whitespace-nowrap rounded-md bg-primary-500 px-3 py-1.5 text-xs font-semibold text-background-50 transition-colors hover:bg-primary-600"
              >
                搜索
              </button>
            </div>
          </form>
        </div>
      </section>

      <section className="w-full px-4 py-8 md:px-8 md:py-10">
        <div className="mb-6 flex flex-wrap gap-2">
          {categories.map((c) => {
            const active = c.key === activeCat;
            return (
              <button
                key={c.key}
                type="button"
                onClick={() => selectCategory(c.key)}
                aria-pressed={active}
                className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border px-3.5 py-2 text-sm font-medium transition-colors ${
                  active
                    ? "border-primary-500 bg-primary-500 text-background-50"
                    : "border-background-200 bg-background-50 text-foreground-700 hover:border-primary-300 hover:bg-primary-50 hover:text-primary-700"
                }`}
              >
                <i className={`${c.icon} text-base`}></i>
                {c.name}
              </button>
            );
          })}
        </div>

        <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-foreground-600">
            共找到 <span className="font-semibold text-foreground-950">{filtered.length}</span> 个围观现场
            {query && (
              <span className="ml-1">
                · 关键词「<span className="font-semibold text-primary-600">{query}</span>」
              </span>
            )}
          </p>
          {(activeCat !== "all" || query) && (
            <button
              type="button"
              onClick={() => {
                setInput("");
                setSearchParams(new URLSearchParams());
              }}
              className="inline-flex items-center gap-1 whitespace-nowrap text-sm font-medium text-foreground-500 transition-colors hover:text-primary-600"
            >
              <i className="ri-refresh-line text-base"></i>
              重置筛选
            </button>
          )}
        </div>

        {filtered.length > 0 ? (
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {filtered.map((item) => (
              <EventCard key={item.id} event={item} />
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-16 text-center">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-foreground-500">
              <i className="ri-emotion-sad-line text-3xl"></i>
            </span>
            <h2 className="mt-4 font-heading text-lg font-bold text-foreground-950">
              这个瓜暂时还没熟
            </h2>
            <p className="mt-2 max-w-md text-sm text-foreground-600">
              没有找到匹配的事件，换个关键词或分类试试，也可以直接返回全部现场慢慢逛。
            </p>
            <button
              type="button"
              onClick={() => {
                setInput("");
                setSearchParams(new URLSearchParams());
              }}
              className="mt-5 inline-flex items-center whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-arrow-go-back-line mr-1.5 text-base"></i>
              查看全部事件
            </button>
          </div>
        )}
      </section>
    </Layout>
  );
}